/**
 * Photos taken offline (slips, bags, seals, declarations). Each photo gets a UUIDv7 that the
 * command stores in its photo column; the bytes go up separately with PUT /media/{id}.
 */
import { newId } from '@/lib/ids'
import { getDb, type MediaRecord, type SupplyChainDB } from './db'
import { backoffDelay } from './outbox'

export async function saveMedia(blob: Blob, db: SupplyChainDB = getDb()): Promise<string> {
  const id = newId()
  await db.media.add({
    id,
    blob,
    mime: blob.type || 'image/jpeg',
    size: blob.size,
    created_at: Date.now(),
    status: 'PENDING',
    attempts: 0,
    next_attempt_at: 0,
    last_error: null,
    uploaded_at: null,
  })
  return id
}

export async function getMediaBlob(id: string, db: SupplyChainDB = getDb()): Promise<Blob | null> {
  return (await db.media.get(id))?.blob ?? null
}

/** Remove a photo that was retaken before any command referenced it. */
export async function discardMedia(id: string, db: SupplyChainDB = getDb()): Promise<void> {
  const rec = await db.media.get(id)
  if (rec && rec.status !== 'UPLOADED') await db.media.delete(id)
}

export type MediaUploader = (rec: MediaRecord) => Promise<void>

export interface UploadOutcome {
  uploaded: string[]
  failed: string[]
}

/** A photo upload failed because the phone could not reach the server (not a server refusal). */
function isNetworkFailure(e: unknown): boolean {
  const kind = (e as { kind?: string } | null)?.kind
  return kind === 'network' || kind === 'timeout' || e instanceof TypeError
}

/**
 * Upload the given photos (or every due photo when `ids` is omitted). Already-uploaded ids are
 * skipped. Failures get backoff, and a photo still backing off is not tried again until it is due
 * (`force` tries it now: an online screen waiting on that one photo). On a weak connection a
 * round ends early instead of waiting out every photo's timeout in turn: after two network
 * failures in a row, or once `deadline` (epoch ms) has passed. The rest wait for the next round.
 */
export async function uploadMedia(
  upload: MediaUploader,
  opts: {
    ids?: string[]
    now?: number
    random?: () => number
    force?: boolean
    deadline?: number
  } = {},
  db: SupplyChainDB = getDb(),
): Promise<UploadOutcome> {
  const now = opts.now ?? Date.now()
  const rows = opts.ids
    ? ((await db.media.bulkGet(opts.ids)).filter(Boolean) as MediaRecord[])
    : await db.media.where('status').anyOf(['PENDING', 'ERROR']).sortBy('created_at')

  const outcome: UploadOutcome = { uploaded: [], failed: [] }
  let networkFailures = 0
  for (const rec of rows) {
    if (rec.status === 'UPLOADED') {
      outcome.uploaded.push(rec.id)
      continue
    }
    if (!opts.force && rec.next_attempt_at > now) continue
    if (opts.deadline !== undefined && Date.now() >= opts.deadline) break
    if (!rec.blob) {
      outcome.failed.push(rec.id)
      continue
    }
    // Read the bytes first. A stored photo the phone can no longer read is LOST (retrying cannot
    // help); one it can read goes up from this fresh copy (on an iPhone, sending the stored Blob
    // itself can fail as "network unavailable" when its file is damaged).
    let fresh: Blob
    try {
      const bytes = await readBytes(rec.blob)
      fresh = bytes ? new Blob([bytes], { type: rec.mime || 'image/jpeg' }) : rec.blob
    } catch (e) {
      await db.media.update(rec.id, {
        status: 'LOST',
        attempts: rec.attempts + 1,
        last_error: `Photo file unreadable on this phone: ${e instanceof Error ? e.message : String(e)}`,
      })
      outcome.failed.push(rec.id)
      continue
    }
    try {
      await db.media.update(rec.id, { status: 'UPLOADING' })
      await upload({ ...rec, blob: fresh })
      // Keep the row as an audit trail; drop the bytes.
      await db.media.update(rec.id, {
        status: 'UPLOADED',
        uploaded_at: Date.now(),
        blob: null,
        last_error: null,
      })
      outcome.uploaded.push(rec.id)
      networkFailures = 0
    } catch (e) {
      const attempts = rec.attempts + 1
      await db.media.update(rec.id, {
        status: 'ERROR',
        attempts,
        next_attempt_at: now + backoffDelay(attempts, opts.random),
        last_error: e instanceof Error ? e.message : String(e),
      })
      outcome.failed.push(rec.id)
      // The connection is down or too weak: the next photo would only wait out its timeout too.
      if (isNetworkFailure(e) && ++networkFailures >= 2) break
    }
  }
  return outcome
}

/**
 * Photos left "uploading" by an app that was closed or killed mid-upload go back in the queue.
 * Run once at start, before any upload of this run begins.
 */
export async function recoverStuckMedia(db: SupplyChainDB = getDb()): Promise<number> {
  return db.media
    .where('status')
    .equals('UPLOADING')
    .modify({ status: 'PENDING', next_attempt_at: 0 })
}

/**
 * The bytes of a stored photo; throws when the phone can no longer read them. Null where Blob has
 * no arrayBuffer (very old WebViews): the stored Blob is then sent as it is.
 */
async function readBytes(blob: Blob): Promise<ArrayBuffer | null> {
  return typeof blob.arrayBuffer === 'function' ? blob.arrayBuffer() : null
}

/** Photos whose bytes this phone can no longer read. */
export async function countLostMedia(db: SupplyChainDB = getDb()): Promise<number> {
  return db.media.where('status').equals('LOST').count()
}

/**
 * Try a LOST photo again (a person asked to): back in the queue. If its bytes still cannot be
 * read it goes straight back to LOST on the next send.
 */
export async function retryLostMedia(ids: string[], db: SupplyChainDB = getDb()): Promise<void> {
  await db.media
    .where('id')
    .anyOf(ids)
    .filter((m) => m.status === 'LOST')
    .modify({ status: 'PENDING', next_attempt_at: 0 })
}

/** Media rows whose upload has not finished. Missing ids (never saved) are reported as missing. */
export async function mediaNotUploaded(
  ids: string[],
  db: SupplyChainDB = getDb(),
): Promise<string[]> {
  if (ids.length === 0) return []
  const rows = await db.media.bulkGet(ids)
  return ids.filter((_, i) => rows[i]?.status !== 'UPLOADED')
}

export async function countPendingMedia(db: SupplyChainDB = getDb()): Promise<number> {
  return db.media.where('status').anyOf(['PENDING', 'UPLOADING', 'ERROR']).count()
}
